import { ConfigurationWithMandatoryBuildabilityV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import axios, { AxiosResponse } from 'axios';
import { getVicciApiKey, getVicciServiceConfig } from 'src/feature-app';
import { getMarketingCode } from '@volkswagen-onehub/library-services-marketingcode';
import { } from '@volkswagen-onehub/financial-data-feature-service';
import { reportErrorSentry } from 'src/bff';

export interface VehicleConfiguration {
  salesgroupKey: string;
  salesgroupName: string;
  modelFilters: any[];
  modelKey: string;
  modelName: string;
  carlineKey: string;
  carlineName: string;
  modelVersion: string;
  modelYear: string;
  exterior: { code: string; name: string };
  interior: { code: string; name: string };
  features: any[];
}
export interface VicciData {
  consolidatedData: {
    vehicleConfiguration: VehicleConfiguration;
  };
  priceData: { body: { amount: number } };
  marketingCode: string;
  featuresData: {
    vehicleCatalogue: { features: any[] };
  };
}

export const getVicciData = async (
  config: ConfigurationWithMandatoryBuildabilityV2
): Promise<{ vicciData: VicciData }> => {
  const tenantDataVersion = await getTenantData();

  if (!tenantDataVersion) {
    return { vicciData: { consolidatedData: null, priceData: null, marketingCode: null, featuresData: null } };
  }
  const consolidatedData = await getConsolidatedData(tenantDataVersion, config);
  const priceData = await getPriceData(tenantDataVersion, config);
  const marketingCode = await getMarketingCodeString(tenantDataVersion, config);
  const featuresData = await getFeaturesData(tenantDataVersion, config);

  return { vicciData: { consolidatedData, priceData, marketingCode, featuresData } };
};

export const getPriceDataWithMarketingCode = async (marketingCode: any) => {

  const vicci = getVicciServiceConfig();
  const oneApiKey = getVicciApiKey();

  const serviceConfig = {
    ...vicci,
    headers: { 'x-api-key': oneApiKey },
  };

  const tenantDataVersion = await axios.get(`${vicci.urlOrigin}/vcs/tenantDataVersion?tenant=ihdcc-vw-es-es`, {
    headers: serviceConfig.headers
  }
  );
  try {
    const priceAdapter = await axios.post(
      `${vicci.urlOrigin}/pcs/priceAdapter/configuration?tenant=ihdcc-vw-es-es&tenantDataVersion=${tenantDataVersion.data.tenantDataVersion}`,
      {
        ...marketingCode.vehicleConfiguration,
      },
      {
        headers: serviceConfig.headers
      }
    );
    return priceAdapter.data;
  } catch (error) {
    reportErrorSentry(error);
    console.error('Error retrieving price data: ' + error);
    return null;
  }
};

const getTenantData = async () => {
  const vicci = getVicciServiceConfig();
  const oneApiKey = getVicciApiKey();

  const serviceConfig = {
    ...vicci,
    headers: { 'x-api-key': oneApiKey },
  };

  try {
    const vicci = getVicciServiceConfig();
    const response = await axios.get(`${vicci.urlOrigin}/vcs/tenantDataVersion?tenant=${vicci.tenantPrivate}`, {
      withCredentials: true,
      auth: { username: vicci.credentials.username, password: vicci.credentials.password },
      headers: serviceConfig.headers,
    });

    return response.data.tenantDataVersion;
  } catch (error) {
    reportErrorSentry(error);
    console.error('Error retrieving tenant data: ' + error);
    return null;
  }
};

const getConsolidatedData = async (tenantDataVersion: string, config: ConfigurationWithMandatoryBuildabilityV2) => {
  try {
    const vicci = getVicciServiceConfig();
    const oneApiKey = getVicciApiKey();

    const serviceConfig = {
      ...vicci,
      headers: { 'x-api-key': oneApiKey },
    };

    const vehicleConfiguration = getVehicleConfigForConsolidatedData(config);

    const consolidatedData = await axios.post(
      `${vicci.urlOrigin}/vcs/consolidatedData?tenant=${vicci.tenantPrivate}&tenantDataVersion=${tenantDataVersion}&fetchTechnical=true&fetchEco=false&fetchMandatory=false&fetchPrices=false&fetchWltp=false&fetchPackageContents=false&fetchMediaForConfiguration=false`,
      {
        vehicleConfiguration,
      },
      {
        withCredentials: true,
        auth: { username: vicci.credentials.username, password: vicci.credentials.password },
        headers: serviceConfig.headers,
      },
    );
    return consolidatedData.data;
  } catch (error) {
    reportErrorSentry(error);
    console.error('Error retrieving consolidated data: ' + error);
    return null;
  }
};

const getPriceData = async (tenantDataVersion: string, config: ConfigurationWithMandatoryBuildabilityV2) => {
  try {
    const vicci = getVicciServiceConfig();

    const vehicleConfiguration = getVehicleConfigForPriceData(config);
    const priceAdapter = await axios.post(
      `${vicci.urlOrigin}/pcs/priceAdapter/configuration?tenant=${vicci.tenantPrivate}&tenantDataVersion=${tenantDataVersion}`,
      {
        ...vehicleConfiguration,
      },
      { withCredentials: true, auth: { username: vicci.credentials.username, password: vicci.credentials.password } }
    );

    return priceAdapter.data;
  } catch (error) {
    reportErrorSentry(error);
    console.error('Error retrieving price data: ' + error);
    return null;
  }
};

const getMarketingCodeString = async (tenantDataVersion: string, config: ConfigurationWithMandatoryBuildabilityV2) => {
  try {
    const vicci = getVicciServiceConfig();

    const serviceConfig = {
      name: 'vicci-vgs',
      origin: vicci.urlOrigin,
      path: '/vgs',
      authentication: {
        username: vicci.credentials.username,
        password: vicci.credentials.password,
      },
    };

    const configuration = {
      configuration: config,
      tenant: vicci.tenantPrivate,
      language: 'es',
      ttl: 129600,
      dataversion: tenantDataVersion,
    };

    const marketingCode = await getMarketingCode(serviceConfig, {}, configuration);

    return marketingCode.marketingcode;
  } catch (error) {
    reportErrorSentry(error);
    console.error('Error retrieving marketing code data: ' + error);
    return null;
  }
};

const getFeaturesData = async (tenantDataVersion: string, config: ConfigurationWithMandatoryBuildabilityV2) => {
  try {
    const vicci = getVicciServiceConfig();

    const vehicleConfiguration = getVehicleConfigForConsolidatedData(config);
    const featuresData = await axios.post(
      `${vicci.urlOrigin}/vcs/featuresAndColorsForConfiguration?tenant=${vicci.tenantPrivate}&tenantDataVersion=${tenantDataVersion}&fetchMandatory=false&fetchPrices=false&fetchPackageContents=true&fetchMediaForConfiguration=false&fetchMediaForFeatures=false&buildabilityCheckType=default&fetchFeatureGroups=true&colorsMandatory=false`,
      {
        vehicleConfiguration,
      },
      { withCredentials: true, auth: { username: vicci.credentials.username, password: vicci.credentials.password } }
    );
    return featuresData.data;
  } catch (error) {
    reportErrorSentry(error);
    console.error('Error retrieving features data: ' + error);
    return null;
  }
};

const getVehicleConfigForConsolidatedData = (config: ConfigurationWithMandatoryBuildabilityV2) => {
  return {
    carlineKey: config.carlineId,
    salesgroupKey: config.salesGroupId,
    modelKey: config.modelId,
    modelYear: config.modelYear,
    modelVersion: config.modelVersion,
    exterior: {
      code: config.exteriorId,
    },
    interior: {
      code: config.interiorId,
    },
    features: getFeatures(config.options),
  };
};
const getVehicleConfigForPriceData = (config: ConfigurationWithMandatoryBuildabilityV2) => {
  return {
    carlineKey: config.carlineId,
    exterior: {
      code: config.exteriorId,
      isUserSelected: true,
    },
    features: getFeatures(config.options),
    interior: {
      code: config.interiorId,
      isUserSelected: true,
    },
    modelKey: config.modelId,
    modelVersion: config.modelVersion,
    modelYear: config.modelYear,
    salesgroupKey: config.salesGroupId,
  };
};

const getFeatures = (features: string[]) => {
  return features.map((feature) => {
    return { code: feature };
  });
};
